type Card = { id: string; rank: string; suit: "clubs" | "diamonds" | "hearts" | "spades" };
type Seat = {
  cards: Card[];
  category: string | null;
  netMinor: string | null;
  seatId: string;
  winner: boolean;
};

const SUITS = { clubs: "♣", diamonds: "♦", hearts: "♥", spades: "♠" } as const;
const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

function card(value: unknown): Card | null {
  if (
    !isRecord(value) ||
    typeof value.rank !== "string" ||
    typeof value.suit !== "string" ||
    !(value.suit in SUITS)
  ) {
    return null;
  }
  return {
    id: typeof value.id === "string" ? value.id : `${value.rank}:${value.suit}`,
    rank: value.rank,
    suit: value.suit as Card["suit"],
  };
}

function parse(value: unknown): { board: Card[]; rakeMinor: string; seats: Seat[] } | null {
  if (!isRecord(value) || value.game !== "poker" || !Array.isArray(value.board)) return null;
  const board = value.board.map(card).filter((item): item is Card => item !== null);
  const seats = Array.isArray(value.seats)
    ? value.seats.flatMap((seat): Seat[] => {
        if (!isRecord(seat) || typeof seat.seatId !== "string" || !Array.isArray(seat.cards)) {
          return [];
        }
        return [
          {
            cards: seat.cards.map(card).filter((item): item is Card => item !== null),
            category: typeof seat.category === "string" ? seat.category : null,
            netMinor: typeof seat.netMinor === "string" ? seat.netMinor : null,
            seatId: seat.seatId,
            winner: seat.winner === true,
          },
        ];
      })
    : [];
  return {
    board,
    rakeMinor: typeof value.rakeMinor === "string" ? value.rakeMinor : "0",
    seats,
  };
}

function PlayingCard({ value }: { value: Card }) {
  const red = value.suit === "diamonds" || value.suit === "hearts";
  return (
    <span
      aria-label={`${value.rank} of ${value.suit}`}
      className={`playing-card${red ? " playing-card-red" : ""}`}
      role="img"
    >
      <span>{value.rank}</span>
      <span aria-hidden="true">{SUITS[value.suit]}</span>
    </span>
  );
}

export function PokerRound({ terminalState }: { terminalState: unknown }) {
  const state = parse(terminalState);
  if (!state) return <p className="empty">Poker hand details are unavailable.</p>;
  return (
    <section aria-label="Poker table result" className="blackjack-result">
      <div className="blackjack-hand">
        <div className="blackjack-hand-head">
          <strong>Board</strong>
          <span className="mono-12 faint">Rake {state.rakeMinor} minor</span>
        </div>
        <div className="playing-cards">
          {state.board.map((item) => (
            <PlayingCard key={item.id} value={item} />
          ))}
        </div>
      </div>
      <div className="blackjack-divider">Showdown</div>
      {state.seats.map((seat) => (
        <div className="blackjack-hand" key={seat.seatId}>
          <div className="blackjack-hand-head">
            <strong className="mono-12">{seat.seatId}</strong>
            <span className="blackjack-hand-total">
              {seat.category ?? "Mucked"}
              {seat.winner ? <span className="badge hand-blackjack">Winner</span> : null}
              {seat.netMinor !== null ? (
                <span className="mono-12">{seat.netMinor} minor</span>
              ) : null}
            </span>
          </div>
          <div className="playing-cards">
            {seat.cards.map((item) => (
              <PlayingCard key={item.id} value={item} />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
